import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { bankingPartnerService } from '../services/bankingPartnerService';
import { MerchantPricingTemplate, PricingDetails } from '../types/merchantPricing';

interface PricingTemplateFormProps {
  partnerId: string;
  templateId?: string; // If provided, editing existing template
  onBack: () => void;
  onSaved: () => void;
}

export function PricingTemplateForm({
  partnerId,
  templateId,
  onBack,
  onSaved,
}: PricingTemplateFormProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [pricingType, setPricingType] = useState<MerchantPricingTemplate['pricingType']>('interchangePlus');
  const [riskType, setRiskType] = useState<MerchantPricingTemplate['riskType']>('lowRisk');
  const [isDefault, setIsDefault] = useState(false);
  const [pricing, setPricing] = useState<PricingDetails>({
    processingFees: {
      visaMasterDiscover: 0,
      amex: 0,
    },
    transactionFees: {
      visaMasterDiscover: 0,
      amex: 0,
    },
    avsFee: 0,
    binFee: 0,
    chargebackFee: 0,
    monthlyFee: 0,
    monthlyMinimumFee: 0,
    pciFee: 0,
    retrievalFee: 0,
    revenueSharePercentage: 0,
    sponsorFee: 0,
  });

  // Fetch existing template if editing
  const { data: existingTemplate, isLoading: isLoadingTemplate } = useQuery({
    queryKey: ['pricingTemplate', partnerId, templateId],
    queryFn: () => bankingPartnerService.getPricingTemplate(partnerId, templateId!),
    enabled: !!templateId,
  });

  // Fetch bank costs for reference
  const { data: bankCosts, isLoading: isLoadingCosts } = useQuery({
    queryKey: ['bankCosts', partnerId],
    queryFn: () => bankingPartnerService.getBankCosts(partnerId),
    enabled: !!partnerId,
  });

  useEffect(() => {
    if (existingTemplate) {
      setName(existingTemplate.name);
      setPricingType(existingTemplate.pricingType);
      setRiskType(existingTemplate.riskType);
      setIsDefault(existingTemplate.isDefault || false);
      setPricing(existingTemplate.pricing);
    } else if (bankCosts) {
      // Initialize with bank costs + markup for new templates
      const costs = bankCosts[riskType];
      setPricing({
        processingFees: {
          visaMasterDiscover: costs.processingFees.visaMasterDiscover + 0.5,
          amex: costs.processingFees.amex + 0.5,
        },
        transactionFees: {
          visaMasterDiscover: costs.transactionFees.visaMasterDiscover + 0.05,
          amex: costs.transactionFees.amex + 0.05,
        },
        avsFee: costs.avsFee,
        binFee: costs.binFee,
        chargebackFee: costs.chargebackFee + 5,
        monthlyFee: costs.monthlyFee + 10,
        monthlyMinimumFee: costs.monthlyMinimumFee,
        pciFee: costs.pciFee,
        retrievalFee: costs.retrievalFee + 5,
        revenueSharePercentage: costs.revenueSharePercentage,
        sponsorFee: costs.sponsorFee,
      });
    }
  }, [existingTemplate, bankCosts, riskType]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const templateData = {
        name,
        pricingType,
        riskType,
        isDefault,
        pricing,
      };

      if (templateId) {
        await bankingPartnerService.updatePricingTemplate(partnerId, templateId, templateData);
      } else {
        await bankingPartnerService.createPricingTemplate(partnerId, templateData);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricingTemplates', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['pricingTemplate', partnerId, templateId] });
      toast({
        title: 'Success',
        description: templateId ? 'Template updated successfully' : 'Template created successfully',
      });
      onSaved();
    },
    onError: (error) => {
      console.error('Error saving template:', error);
      toast({
        title: 'Error',
        description: 'Failed to save template',
        variant: 'destructive',
      });
    },
  });

  const handleSave = () => {
    if (!name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a template name',
        variant: 'destructive',
      });
      return;
    }
    saveMutation.mutate();
  };

  const updatePricingField = (field: string, value: number) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setPricing(prev => ({
        ...prev,
        [parent]: {
          ...(prev as any)[parent],
          [child]: value,
        },
      }));
    } else {
      setPricing(prev => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const calculateMarkup = (merchantPrice: number, bankCost: number, isPercentage: boolean = false) => {
    if (isPercentage) {
      return ((merchantPrice - bankCost) * 100).toFixed(2) + ' bps';
    }
    return '$' + (merchantPrice - bankCost).toFixed(2);
  };

  if (isLoadingTemplate || isLoadingCosts) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const costs = bankCosts?.[riskType];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {templateId ? 'Edit' : 'Create'} Pricing Template
            </h1>
            <p className="text-muted-foreground">
              {templateId ? 'Update pricing template configuration' : 'Create a new merchant pricing template'}
            </p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Template
            </>
          )}
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {/* Template Configuration */}
          <Card>
            <CardHeader>
              <CardTitle>Template Configuration</CardTitle>
              <CardDescription>
                Set the template name and pricing model
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Template Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Standard Low Risk Interchange+"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div>
                <Label>Pricing Type</Label>
                <Select value={pricingType} onValueChange={(value: any) => setPricingType(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="interchangePlus">Interchange Plus</SelectItem>
                    <SelectItem value="surcharge">Surcharge</SelectItem>
                    <SelectItem value="tiered">Tiered</SelectItem>
                    <SelectItem value="flatRate">Flat Rate</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Risk Type</Label>
                <RadioGroup value={riskType} onValueChange={(value: any) => setRiskType(value)}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="lowRisk" id="lowRisk" />
                    <Label htmlFor="lowRisk">Low Risk</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="highRisk" id="highRisk" />
                    <Label htmlFor="highRisk">High Risk</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isDefault"
                  checked={isDefault}
                  onCheckedChange={(checked) => setIsDefault(checked as boolean)}
                />
                <Label htmlFor="isDefault" className="cursor-pointer">
                  Set as default template
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Processing Fees */}
          <Card>
            <CardHeader>
              <CardTitle>Processing Fees</CardTitle>
              <CardDescription>
                Configure processing fees for different card types
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Visa/MasterCard/Discover (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={pricing.processingFees.visaMasterDiscover}
                    onChange={(e) => updatePricingField('processingFees.visaMasterDiscover', parseFloat(e.target.value) || 0)}
                  />
                  {costs && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Bank cost: {costs.processingFees.visaMasterDiscover}% |
                      Markup: {calculateMarkup(pricing.processingFees.visaMasterDiscover, costs.processingFees.visaMasterDiscover, true)}
                    </p>
                  )}
                </div>
                <div>
                  <Label>American Express (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={pricing.processingFees.amex}
                    onChange={(e) => updatePricingField('processingFees.amex', parseFloat(e.target.value) || 0)}
                  />
                  {costs && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Bank cost: {costs.processingFees.amex}% |
                      Markup: {calculateMarkup(pricing.processingFees.amex, costs.processingFees.amex, true)}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Transaction Fees */}
          <Card>
            <CardHeader>
              <CardTitle>Transaction Fees</CardTitle>
              <CardDescription>
                Configure per-transaction fees
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Visa/MasterCard/Discover ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={pricing.transactionFees.visaMasterDiscover}
                    onChange={(e) => updatePricingField('transactionFees.visaMasterDiscover', parseFloat(e.target.value) || 0)}
                  />
                  {costs && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Bank cost: ${costs.transactionFees.visaMasterDiscover} |
                      Markup: {calculateMarkup(pricing.transactionFees.visaMasterDiscover, costs.transactionFees.visaMasterDiscover)}
                    </p>
                  )}
                </div>
                <div>
                  <Label>American Express ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={pricing.transactionFees.amex}
                    onChange={(e) => updatePricingField('transactionFees.amex', parseFloat(e.target.value) || 0)}
                  />
                  {costs && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Bank cost: ${costs.transactionFees.amex} |
                      Markup: {calculateMarkup(pricing.transactionFees.amex, costs.transactionFees.amex)}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Fees */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Fees</CardTitle>
              <CardDescription>
                Configure various additional fees and charges
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Monthly Fee ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={pricing.monthlyFee}
                    onChange={(e) => updatePricingField('monthlyFee', parseFloat(e.target.value) || 0)}
                  />
                  {costs && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Bank cost: ${costs.monthlyFee} |
                      Markup: {calculateMarkup(pricing.monthlyFee, costs.monthlyFee)}
                    </p>
                  )}
                </div>
                <div>
                  <Label>Monthly Minimum Fee ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={pricing.monthlyMinimumFee}
                    onChange={(e) => updatePricingField('monthlyMinimumFee', parseFloat(e.target.value) || 0)}
                  />
                  {costs && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Bank cost: ${costs.monthlyMinimumFee}
                    </p>
                  )}
                </div>
                <div>
                  <Label>Chargeback Fee ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={pricing.chargebackFee}
                    onChange={(e) => updatePricingField('chargebackFee', parseFloat(e.target.value) || 0)}
                  />
                  {costs && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Bank cost: ${costs.chargebackFee} |
                      Markup: {calculateMarkup(pricing.chargebackFee, costs.chargebackFee)}
                    </p>
                  )}
                </div>
                <div>
                  <Label>Retrieval Fee ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={pricing.retrievalFee}
                    onChange={(e) => updatePricingField('retrievalFee', parseFloat(e.target.value) || 0)}
                  />
                  {costs && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Bank cost: ${costs.retrievalFee} |
                      Markup: {calculateMarkup(pricing.retrievalFee, costs.retrievalFee)}
                    </p>
                  )}
                </div>
                <div>
                  <Label>AVS Fee ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={pricing.avsFee}
                    onChange={(e) => updatePricingField('avsFee', parseFloat(e.target.value) || 0)}
                  />
                  {costs && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Bank cost: ${costs.avsFee}
                    </p>
                  )}
                </div>
                <div>
                  <Label>BIN Fee ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={pricing.binFee}
                    onChange={(e) => updatePricingField('binFee', parseFloat(e.target.value) || 0)}
                  />
                  {costs && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Bank cost: ${costs.binFee}
                    </p>
                  )}
                </div>
                <div>
                  <Label>PCI Fee ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={pricing.pciFee}
                    onChange={(e) => updatePricingField('pciFee', parseFloat(e.target.value) || 0)}
                  />
                  {costs && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Bank cost: ${costs.pciFee}
                    </p>
                  )}
                </div>
                <div>
                  <Label>Sponsor Bank Fee ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={pricing.sponsorFee}
                    onChange={(e) => updatePricingField('sponsorFee', parseFloat(e.target.value) || 0)}
                  />
                  {costs && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Bank cost: ${costs.sponsorFee}
                    </p>
                  )}
                </div>
              </div>

              <Separator />

              <div>
                <Label>Revenue Share Percentage (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={pricing.revenueSharePercentage}
                  onChange={(e) => updatePricingField('revenueSharePercentage', parseFloat(e.target.value) || 0)}
                />
                {costs && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Bank revenue share: {costs.revenueSharePercentage}%
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Summary Sidebar */}
        <div>
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle>Template Summary</CardTitle>
              <CardDescription>
                Overview of configured pricing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium">Template Name</p>
                <p className="text-lg font-bold">{name || '(Not set)'}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Pricing Model</p>
                <p className="text-lg capitalize">{pricingType.replace(/([A-Z])/g, ' $1').trim()}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Risk Type</p>
                <p className="text-lg capitalize">{riskType.replace(/([A-Z])/g, ' $1').trim()}</p>
              </div>
              <Separator />
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Processing (V/MC/D)</span>
                  <span className="font-medium">{pricing.processingFees.visaMasterDiscover}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Processing (Amex)</span>
                  <span className="font-medium">{pricing.processingFees.amex}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Transaction (V/MC/D)</span>
                  <span className="font-medium">${pricing.transactionFees.visaMasterDiscover.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Transaction (Amex)</span>
                  <span className="font-medium">${pricing.transactionFees.amex.toFixed(2)}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span>Monthly Fee</span>
                  <span className="font-medium">${pricing.monthlyFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Chargeback Fee</span>
                  <span className="font-medium">${pricing.chargebackFee.toFixed(2)}</span>
                </div>
              </div>
              {isDefault && (
                <>
                  <Separator />
                  <div className="flex items-center gap-2 text-sm text-yellow-600">
                    <span className="font-medium">⭐ Default Template</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
